import { z } from 'zod';
import type { TipoAutorizacion } from '../permisos.ts';
import { autorizacionesAdjuntas } from './autorizaciones.ts';
import { decimalPositivo, uuid } from './comun.ts';
import { lineaVentaInput, pagoLineaInput, type DocumentoDTO } from './ventas.ts';

export const devolucionLineaInput = z.object({
  documento_linea_id: uuid,
  /** Unidades devueltas (unidad de inventario). */
  cantidad: decimalPositivo,
  /** Producto dañado: entra igual al almacén y se sugiere ajuste de rotura. */
  danada: z.boolean().default(false),
});

/**
 * Reemplazo en el mismo acto (cambio directo 24-09-2026; factura nueva 2026-10-09): lo que el cliente se lleva.
 *  - Origen NOTA → «Cambio» + NOTA de despacho pagada con el crédito del cambio.
 *  - Origen FACTURA → nota de crédito + FACTURA nueva pagada con el saldo a favor que deja la nota de crédito.
 * Si lo que se lleva vale más, la diferencia se paga con `pagos`; si vale menos, el resto queda como saldo a favor.
 */
export const despachoCambioInput = z.object({
  lineas: z.array(lineaVentaInput).min(1, 'Indica qué se despacha').max(100),
  pagos: z.array(pagoLineaInput).max(10).default([]),
  /** Entregar ya lo que está en la tienda (por defecto) o dejarlo pendiente en Entregas. */
  entregar_ahora: z.boolean().default(true),
  /** Factura nueva a un cliente especial: modalidad de retención, igual que en el punto de venta. */
  modalidad_retencion: z.enum(['base25', 'completo']).optional(),
});
export type DespachoCambioInput = z.infer<typeof despachoCambioInput>;

export const devolucionInput = z.object({
  documento_origen_id: uuid,
  /**
   * El documento lo decide el origen: FACTURA → nota de crédito fiscal · NOTA → «Cambio» interno. Se acepta por
   * compatibilidad; pedir «cambio» para una factura se rechaza (el cambio informal de facturas se retiró).
   */
  modo: z.enum(['nc', 'cambio']).optional(),
  /** Almacén receptor de la mercancía. */
  almacen_id: uuid,
  lineas: z.array(devolucionLineaInput).min(1, 'Indica qué se devuelve').max(200),
  motivo: z.string().trim().min(3, 'Indica el motivo').max(300),
  /**
   * Excepción: devolver el dinero en vez de dejarlo como saldo a favor. Requiere el permiso
   * `devoluciones.reintegro-dinero` o una autorización `devolucion_dinero`. No sale de la caja: queda un reintegro
   * pendiente que la dueña paga por transferencia.
   */
  devolver_dinero: z.boolean().default(false),
  observaciones: z.string().trim().max(500).optional(),
  /** Lo que el cliente se lleva en el mismo acto (nota de despacho o factura nueva). */
  despacho: despachoCambioInput.optional(),
  /** Autorizaciones adjuntas: devolución fuera de plazo, devolución de dinero. */
  autorizaciones: autorizacionesAdjuntas,
});
export type DevolucionInput = z.infer<typeof devolucionInput>;

export const calcularDevolucionInput = devolucionInput.pick({ documento_origen_id: true, modo: true, lineas: true });

/**
 * «Facturar a otro nombre» (spec 08, enmienda 2026-10-29): nota de crédito por el total de la factura (o Cambio de la nota
 * de entrega), sin mover inventario, y factura (o nota) nueva al cliente nuevo con la misma información de la original,
 * pagada con el crédito de esa nota. Permiso `ventas.otro-nombre` o la autorización `otro_nombre`.
 */
export const otroNombreInput = z.object({
  documento_origen_id: uuid,
  /** El cliente a cuyo nombre sale el documento nuevo (la empresa con su RIF). */
  cliente_id: uuid,
  motivo: z.string().trim().min(3, 'Indica el motivo').max(300),
  autorizaciones: autorizacionesAdjuntas,
});
export type OtroNombreInput = z.infer<typeof otroNombreInput>;

/** La dueña registra la transferencia con la que devolvió el dinero. */
export const reintegroPagarInput = z.object({
  banco: z.string().trim().min(2, 'Indica el banco').max(60),
  referencia: z.string().trim().min(4, 'Referencia de la transferencia (mínimo 4 caracteres)').max(40),
  nota: z.string().trim().max(200).optional(),
});
export type ReintegroPagarInput = z.infer<typeof reintegroPagarInput>;
export const reintegrosFiltros = z.object({ estado: z.enum(['pendiente', 'pagado']).optional() });

export interface ReintegroDTO {
  id: string;
  sucursal_nombre: string;
  documento_id: string;
  documento_tipo: 'NC' | 'CAM';
  documento_numero: string;
  origen_numero: string | null;
  cliente_razon_social: string;
  cliente_rif: string;
  cliente_telefono: string | null;
  monto_usd: string;
  /** Equivalente en Bs a la tasa del documento (referencia para la transferencia). */
  monto_bs: string;
  tasa: string;
  motivo: string;
  estado: 'pendiente' | 'pagado';
  solicitado_por_nombre: string;
  solicitado_en: string;
  autorizado_por_nombre: string;
  banco: string | null;
  referencia: string | null;
  nota: string | null;
  pagado_por_nombre: string | null;
  pagado_en: string | null;
}
export type CalcularDevolucionInput = z.infer<typeof calcularDevolucionInput>;

export const notaDebitoLineaInput = z.object({
  producto_id: uuid,
  presentacion_id: uuid,
  cantidad: decimalPositivo,
  /** $ divisa por unidad del cargo (diferencia de precio, flete, etc.); mayor que cero: una ND en cero gastaría número y control. */
  precio_unitario_divisa: decimalPositivo,
});
export const notaDebitoInput = z.object({
  documento_origen_id: uuid,
  lineas: z.array(notaDebitoLineaInput).min(1, 'Indica el cargo').max(50),
  motivo: z.string().trim().min(3, 'Indica el motivo').max(300),
  observaciones: z.string().trim().max(500).optional(),
});
export type NotaDebitoInput = z.infer<typeof notaDebitoInput>;

/** Tope de filas del listado de NC/ND/CAM y de la búsqueda del origen: la pantalla pide el máximo y avisa si la lista llegó al tope. */
export const LIMITE_DEVOLUCIONES = 50;
export const origenFiltros = z.object({ q: z.string().trim().max(80).optional(), limite: z.coerce.number().int().min(1).max(LIMITE_DEVOLUCIONES).default(10) });
/** Listado de NC/ND/CAM: búsqueda y rango de fechas (día de emisión, zona Caracas). Pedido del cliente 03-10-2026. */
export const devolucionesFiltros = origenFiltros.extend({ desde: z.iso.date().optional(), hasta: z.iso.date().optional() });
export type DevolucionesFiltros = z.infer<typeof devolucionesFiltros>;

/** Documento origen con lo devolvible por línea (regla 1: entregado − devuelto > 0). */
export interface OrigenDevolucionDTO {
  id: string;
  tipo: 'FAC' | 'NOTA';
  numero_formateado: string;
  control_formateado: string | null;
  cliente_id: string;
  cliente_razon_social: string;
  cliente_rif: string;
  consumidor_final: boolean;
  emitido_en: string;
  /** Días transcurridos desde la venta y si ya pasó el plazo de devolución (config `devolucion_plazo_dias`). */
  dias_desde_venta: number;
  fuera_de_plazo: boolean;
  tasa_aplicada: string;
  /** Tasa BCV congelada del origen: para mostrar el «$ a BCV» como el POS. */
  tasa_bcv: string;
  /** Factor divisa congelado del origen (reintegro en $ fisico = credito ÷ factor). */
  factor_divisa: string;
  total: string;
  /** Bs del papel del origen. */
  total_bs: string;
  modalidad_retencion: string | null;
  tiene_devolucion: boolean;
  lineas: {
    id: string;
    producto_id: string;
    producto_codigo: string;
    producto_nombre: string;
    presentacion_nombre: string;
    unidades_presentacion: string;
    unidades: string;
    entregado: string;
    devuelto: string;
    devolvible: string;
    precio_unitario: string;
    /** Bs por unidad guardados en la línea del origen. */
    precio_unitario_bs: string;
    descuento_pct: string;
  }[];
}

export interface CalculoDevolucionDTO {
  tipo_resultante: 'NC' | 'CAM';
  /** Documento a Consumidor Final (anterior a la regla): no acumula saldo ni admite reemplazo; solo devolución de dinero autorizada. */
  consumidor_final: boolean;
  dias_desde_venta: number;
  plazo_dias: number;
  fuera_de_plazo: boolean;
  /** Lo que este usuario no puede hacer sin autorización (p. ej. `devolucion_fuera_plazo`). */
  autorizaciones_requeridas: TipoAutorizacion[];
  lineas: {
    documento_linea_id: string;
    producto_codigo: string;
    producto_nombre: string;
    unidades: string;
    precio_unitario: string;
    subtotal: string;
    precio_unitario_bs: string;
    subtotal_bs: string;
    /**
     * Qué es el renglón para el inventario (vista previa de los libros, 2026-10-30): producto con existencia, lote de
     * «productos con detalles», pieza de vidrio o servicio (las dos últimas no entran a los libros de inventario).
     */
    inventario: 'producto' | 'lote' | 'pieza' | 'servicio';
    /** Se marcó «Dañado»: entra y sale de la existencia en el acto. */
    danada: boolean;
  }[];
  bruto: string;
  descuento: string;
  base: string;
  iva_pct: string;
  iva: string;
  total: string;
  /** Bolívares con las tasas congeladas del origen (regla de precios 2026-10-09). */
  base_bs: string;
  iva_bs: string;
  total_bs: string;
  tasa_aplicada: string;
  /** Tasa BCV congelada del origen: para mostrar el «$ a BCV» como el POS. */
  tasa_bcv: string;
  retencion_iva: string;
  retencion_municipal: string;
  /** Bs de las retenciones proporcionales (los que guarda la nota de crédito). */
  retencion_iva_bs: string;
  retencion_municipal_bs: string;
  /** Monto que se acredita al saldo a favor (o se reintegra): total − retenciones descontadas. */
  credito: string;
  /** Lo que el cliente aún debe del documento origen (0 si está pagado): el crédito va a saldo a favor igual. */
  saldo_pendiente_origen: string;
  /**
   * Tope del dinero que se puede DEVOLVER (reintegro): lo que el cliente pagó del origen y aún no se le ha devuelto ni
   * acreditado. Pedir devolver más responde 409 `reintegro_excede_pagado`.
   */
  dinero_devolvible: string;
  advertencias: string[];
}

export interface DevolucionResultadoDTO extends DocumentoDTO {
  credito_aplicado: string;
  saldo_a_favor: string | null;
  motivo: string | null;
  /** Devolución de dinero autorizada: queda pendiente hasta que la dueña registra la transferencia. */
  reintegro: { id: string; monto_usd: string; monto_bs: string; estado: 'pendiente' | 'pagado' } | null;
  /** Material devuelto dañado: ajuste «Daño» generado (aprobado o pendiente de quien aprueba ajustes). */
  ajuste_danado: { id: string; numero_formateado: string; estado: string } | null;
  /** Lo que el cliente se llevó: nota de despacho (cambio) o factura nueva (nota de crédito), cuánto del crédito se aplicó y si ya se entregó. */
  despacho: { id: string; tipo: 'NOTA' | 'FAC'; numero_formateado: string; control_formateado: string | null; total: string; total_bs: string; tasa_bcv: string; tasa_aplicada: string; aplicado_cambio: string; estado_entrega: string } | null;
}
